/**
 * Frontend money formatting. NEVER does arithmetic — all math is server-side,
 * see ADR-0005. The amount string from the API arrives at full precision and
 * we hand it directly to Intl.NumberFormat as a Number for display only.
 *
 * Decimal places are driven by the BACKEND per-currency `currencies.exponent`
 * (registered via setCurrencyDecimals on app boot), NOT by the browser's
 * CLDR/ISO minor-units table. This makes the backend the single control —
 * e.g. IQD is configured to 0 decimals (whole dinars) even though ISO says 3.
 * When a currency's exponent isn't registered yet we fall back to CLDR.
 */

import type { NumberingSystem } from './intl.js';

/**
 * Module-level registry of code → decimal places, populated once from
 * GET /currencies (see currenciesStore.load). money.ts stays pure/non-React:
 * the store pushes the map in, formatMoney reads it. Keys are uppercased codes.
 */
const decimalsRegistry = new Map<string, number>();

/**
 * Register per-currency decimal places (the backend `currencies.exponent`).
 * Accepts the /currencies array or a plain code→exponent map. Idempotent.
 */
export function setCurrencyDecimals(
  entries: Array<{ code: string; exponent: number }> | Record<string, number>,
): void {
  if (Array.isArray(entries)) {
    for (const e of entries) {
      if (e && typeof e.code === 'string' && typeof e.exponent === 'number') {
        decimalsRegistry.set(e.code.toUpperCase(), e.exponent);
      }
    }
  } else {
    for (const [code, exp] of Object.entries(entries)) {
      if (typeof exp === 'number') decimalsRegistry.set(code.toUpperCase(), exp);
    }
  }
}

/** The backend-configured decimal places for a currency, or undefined if unknown. */
export function getCurrencyDecimals(code: string | null | undefined): number | undefined {
  if (!code) return undefined;
  return decimalsRegistry.get(code.toUpperCase());
}

export interface MoneyDisplayOptions {
  locale: string;
  currency: string;
  /** "latn" (default) or "arab" — tenant/user preference. */
  numberingSystem?: NumberingSystem;
  /** When true, prefixes "≈" — used for converted (non-storage) amounts. */
  approximate?: boolean;
}

/**
 * Collapse the USD currency token to a bare "$". Several locales (e.g. Arabic)
 * render USD as "US$" even with currencyDisplay:'narrowSymbol' because that's
 * their CLDR data; we want a plain "$" for USD app-wide. Targeted to USD only —
 * other dollar currencies (CA$, A$, …) keep their disambiguating prefix.
 */
export function narrowUsd(s: string): string {
  return s.replace(/US\s?\$|\$\s?US/g, '$');
}

/**
 * CLDR default fraction digits for a currency (cached). Lets formatDecimals fall
 * back to the same precision formatMoney would render for an unregistered currency
 * (e.g. IQD → 3) instead of a flat 2. Guards the Intl call (invalid codes throw)
 * and caches per (currency, locale) since it can run per table row.
 */
const cldrDecimalsCache = new Map<string, number>();
function cldrCurrencyDecimals(currency: string | null | undefined, locale: string): number {
  if (!currency) return 2;
  const key = `${currency}|${locale}`;
  let v = cldrDecimalsCache.get(key);
  if (v === undefined) {
    try {
      v = new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
    } catch {
      v = 2;
    }
    cldrDecimalsCache.set(key, v);
  }
  return v;
}

/**
 * A bare grouped number (NO currency symbol) honoring the currency's backend
 * decimals — for money displays that show the amount without a currency token
 * (e.g. the cashback wallet). Falls back to the currency's CLDR default decimals
 * (matching formatMoney) when its exponent isn't registered.
 */
export function formatDecimals(
  amount: string | number,
  currency: string | null | undefined,
  locale: string,
  numberingSystem: NumberingSystem = 'latn',
): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return String(amount);
  const dp = getCurrencyDecimals(currency) ?? cldrCurrencyDecimals(currency, locale);
  // numberingSystem mirrors formatMoney: Latin digits unless a caller explicitly
  // opts into 'arab', so an Arabic UI doesn't turn amounts into ٠١٢٣.
  return value.toLocaleString(locale, {
    numberingSystem,
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
}

/**
 * Some currencies' narrow symbol is an Arabic-script glyph (e.g. IQD → "ع.د",
 * AED → "د.إ", SAR → "﷼") which, especially in an RTL/Arabic UI, reads worse
 * than the plain ISO code. For those we force currencyDisplay:'code' so they
 * render as "IQD" etc. in EVERY locale; Latin-symbol currencies ($/€/£/¥) keep
 * their symbol. Detected once per (currency, locale) and cached.
 */
// Arabic, Arabic Supplement, Presentation Forms A/B — written as escapes because
// the literal range ends at U+FEFF, which reads as stray whitespace in an editor
// (and trips no-irregular-whitespace). Same character set as before.
const ARABIC_SYMBOL_RE = /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/;
const codeDisplayCache = new Map<string, boolean>();
function prefersIsoCode(currency: string, locale: string): boolean {
  const key = `${currency}|${locale}`;
  let v = codeDisplayCache.get(key);
  if (v === undefined) {
    try {
      const sym = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
        .formatToParts(0)
        .find((p) => p.type === 'currency')?.value ?? '';
      v = ARABIC_SYMBOL_RE.test(sym);
    } catch {
      v = false;
    }
    codeDisplayCache.set(key, v);
  }
  return v;
}

/**
 * Strip insignificant trailing zeros (and a dangling dot) from a money STRING,
 * for prefilling editable amount inputs — so an IQD price loads as "8000", not
 * the raw DECIMAL(19,4) "8000.0000". Only trims after a decimal point, so integer
 * trailing zeros are preserved ("100" stays "100"). Not for display — use
 * formatMoney for that.
 */
export function trimMoney(s: string): string {
  if (typeof s !== 'string' || !s.includes('.')) return s;
  return s.replace(/0+$/, '').replace(/\.$/, '');
}

export function formatMoney(amountString: string, opts: MoneyDisplayOptions): string {
  const value = Number(amountString);
  const numberingSystem = opts.numberingSystem ?? 'latn';
  // Backend-configured decimal places win over CLDR when known (e.g. IQD → 0).
  const decimals = getCurrencyDecimals(opts.currency);
  let rendered: string;
  try {
    // Intl.NumberFormat throws a RangeError on an invalid currency code — which
    // happens transiently while a free-text currency field is being typed
    // (e.g. "EU" on the way to "EUR"). Catch it so display never crashes the UI.
    const formatter = new Intl.NumberFormat(opts.locale, {
      style: 'currency',
      currency: opts.currency,
      // Arabic-symbol currencies (IQD, …) → ISO code; others → narrow symbol.
      currencyDisplay: prefersIsoCode(opts.currency, opts.locale) ? 'code' : 'narrowSymbol',
      numberingSystem,
      ...(decimals != null
        ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
        : {}),
    });
    rendered = Number.isFinite(value) ? narrowUsd(formatter.format(value)) : amountString;
  } catch {
    // Fall back to a plain localized number + the raw code.
    const fd = decimals ?? 2;
    let num: string;
    try {
      num = Number.isFinite(value)
        ? new Intl.NumberFormat(opts.locale, { numberingSystem, minimumFractionDigits: fd, maximumFractionDigits: fd }).format(value)
        : amountString;
    } catch {
      num = amountString;
    }
    rendered = `${num} ${opts.currency}`.trim();
  }
  return opts.approximate ? `≈ ${rendered}` : rendered;
}
